import React from 'react';
import PropTypes from 'prop-types';
import {FileUploadV2} from '@pepsico-ds/ui';
import UploadProgressBar from '@/components/atoms/UploadProgressBar';
import ValidationErrorMessage from './ValidationErrorMessage';
import styles from './ActivityGameDisplay.module.scss';

const IMAGE_ACCEPT = '.jpg,.png,.webp';
const IMAGE_DESCRIPTION =
  'Drag and drop your file here (only .webp, .jpg,.png)';
const MAX_SIZE_TOOLTIP = 'Max 70 KB';

const DisplayImageUploadField = ({
  label,
  imageUri,
  progress = 0,
  validationError = '',
  onUpload,
  onDelete,
  onFileError,
  testId,
  validationErrorTestId,
  containerClassName = `max-w-[380px] rounded-lg border border-[color:var(--color-border-default)] p-3 ${styles.imageUploadContainer}`,
  errorPlacement = 'inline',
  wrapperClassName = ''
}) => {
  const uploadField = (
    <div className={containerClassName}>
      <UploadProgressBar
        progress={progress}
        processingLabel='Processing image...'
      />
      <FileUploadV2
        accept={IMAGE_ACCEPT}
        buttonLabel='Upload file'
        description={IMAGE_DESCRIPTION}
        feedbackType={validationError ? 'error' : 'info'}
        label={{
          label,
          showLabelIcon: true,
          infoIconTooltip: {
            text: MAX_SIZE_TOOLTIP
          }
        }}
        type='image'
        variant={imageUri ? 'edit' : 'default'}
        imageUri={imageUri || undefined}
        uploadIcon='image'
        onUpdate={(files) => onUpload(files)}
        onDelete={() => onDelete()}
        onFileError={() => onFileError?.()}
        data-testid={testId}
      />
    </div>
  );

  if (errorPlacement === 'external') {
    return <div className={wrapperClassName}>{uploadField}</div>;
  }

  return (
    <div className={wrapperClassName}>
      {uploadField}
      {validationError && (
        <ValidationErrorMessage
          error={validationError}
          testId={validationErrorTestId}
          className='mt-1 w-full'
        />
      )}
    </div>
  );
};

DisplayImageUploadField.propTypes = {
  label: PropTypes.string.isRequired,
  imageUri: PropTypes.string,
  progress: PropTypes.number,
  validationError: PropTypes.string,
  onUpload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFileError: PropTypes.func,
  testId: PropTypes.string.isRequired,
  validationErrorTestId: PropTypes.string.isRequired,
  containerClassName: PropTypes.string,
  errorPlacement: PropTypes.oneOf(['inline', 'external']),
  wrapperClassName: PropTypes.string
};

export default React.memo(DisplayImageUploadField);
